import { defineStore } from 'pinia'
import { log } from '@/plugins/logger'
import { useAuthStore } from '@/store/auth'
import { db } from '@/plugins/firebase'
import { getDoc, doc } from 'firebase/firestore'

/**
 * Store for managing tenant-specific data and configurations
 */
export const useTenantStore = defineStore('tenant', {
  state: () => ({
    companyConfig: null
  }),
  getters: {
    /**
     * tenantHasEmailPreferences
     *
     * Determines if the tenant has any email preferences set
     * Checks if fields exist because some tenant schemas won't have these fields
     *
     * @returns {Object} An object with properties rules and exampleEmails, both boolean
     */
    tenantHasEmailPreferences() {
      const hasPreferences = this.companyConfig?.tenant_preferences?.ai_email_preferences
      const hasEmailRules = hasPreferences?.rules?.length > 0
      const hasExampleEmails = hasPreferences?.example_emails?.length > 0

      if (!hasPreferences) {
        return {
          rules: false,
          exampleEmails: false
        }
      }
      return {
        rules: hasEmailRules,
        exampleEmails: hasExampleEmails
      }
    }
  },
  actions: {
    /**
     * Fetches company configuration from Firestore and updates the store state
     * @param {Object} options - Configuration options,
     * @param {boolean} options.throwErrors - Whether to throw errors instead of handling them silently, included for backwards compatibility
     */
    async getCompanyConfig(options = { throwErrors: false }) {
      const authStore = useAuthStore()
      const docRef = doc(db, authStore.userData.company, 'company_configurations')
      // get company config
      try {
        let configDoc = await getDoc(docRef)

        if (configDoc.exists()) {
          this.companyConfig = configDoc.data()
        } else {
          log.error('Company config does not exist')
          this.companyConfig = null
          if (options.throwErrors) throw new Error('Company config does not exist')
        }
      } catch (e) {
        log.error('Error getting company config', e)
        if (options.throwErrors) throw e
      }
    },

    /**
     * Clears the company configuration from the store
     */
    clearCompanyConfig() {
      this.companyConfig = null
    },

    /**
     * Checks if value message sequence variables are configured for a specific target destination
     * @param {string} targetDestination - The target destination to check
     * @returns {boolean} True if value message sequence variables are configured, false otherwise
     */
    valueMessageSequenceVariablesConfigured(targetDestination) {
      if (!this.companyConfig || !targetDestination) {
        return false
      }

      log.debug('Checking value message sequence variables configured for', targetDestination)

      const integration = this.companyConfig?.integrations?.[targetDestination]
      const fieldMappings = integration?.field_mappings

      return Boolean(
        integration && fieldMappings?.value_message_subject && fieldMappings?.value_message_body
      )
    }
  }
})
